import { forwardRef, ReactNode } from 'react';
import { chakra, ChakraProps } from '@chakra-ui/react';
import Icon, { TypeIconName } from '../Icon/Icon';
import Avatar, { AvatarProps } from '../Avatar/Avatar';
import { useDropdownStyles } from './Dropdown.context';
import { DropdownProps } from './DropdownProps';

interface DropdownButtonProps extends ChakraProps {
  avatar?: AvatarProps | undefined;
  'aria-label'?: string;
  blurHandler: () => void;
  id?: string;
  readOnly?: boolean;
  placeholder?: string;
  formLabel?: ReactNode;
  size: DropdownProps<string | null>['size'];
  children?: ReactNode;
  iconName?: TypeIconName;
  isWarning?: boolean;
  isOpen?: boolean;
  isError?: boolean;
  disabled?: boolean;
}
const DropdownButton = forwardRef<HTMLButtonElement, DropdownButtonProps>(
  (
    {
      avatar,
      blurHandler,
      children,
      disabled,
      formLabel,
      iconName,
      placeholder,
      readOnly,
      size,
      isWarning,
      isError,
      isOpen,
      ...rest
    },
    ref,
  ) => {
    const { field, buttonContent, buttonContentHover, icon } = useDropdownStyles();
    const iconSize = size === 'lg' ? '24' : '16';

    const chevronStyle = {
      ...icon,
      transform: isOpen ? 'rotate(180deg)' : undefined,
      transition: 'transform 0.2s ease',
    };

    return (
      <chakra.button
        ref={ref}
        __css={field}
        aria-readonly={readOnly}
        disabled={disabled}
        onBlur={blurHandler}
        role={readOnly ? undefined : 'group'}
        type="button"
        {...rest}
      >
        {avatar && <Avatar size={size === 'lg' ? '32' : '24'} {...avatar} />}
        {iconName && <Icon __css={icon} aria-hidden="true" name={iconName} size={iconSize} />}
        <chakra.span
          __css={buttonContent}
          _groupHover={buttonContentHover}
          flexGrow={1}
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          {formLabel || placeholder}
        </chakra.span>
        {!isError && isWarning && <Icon aria-hidden="true" name="WarningYellow" size={iconSize} />}
        {isError && <Icon aria-hidden="true" color="icon/negative" name="ErrorCircleFilled" size={iconSize} />}
        <Icon
          __css={chevronStyle}
          _groupActive={
            readOnly || disabled
              ? undefined
              : {
                  transform: 'rotate(180deg)',
                  transition: 'transform 0.2s ease',
                }
          }
          aria-hidden="true"
          name="SelectChevron"
          size="24"
        />
      </chakra.button>
    );
  },
);

export default DropdownButton;
